import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from './Layout';
import StatusInfo from '../components/StatusInfo';
import ListPager from '../components/ListPager';
import { fmtMoney, plural } from '../utils/format';

const API = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
const LIMIT = 50;

// Генерируем список месяцев: текущий + 11 предыдущих
function getMonthOptions() {
  const options = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    options.push({
      year: d.getFullYear(),
      month: d.getMonth() + 1,
      label: d.toLocaleString('ru', { month: 'long', year: 'numeric' }),
    });
  }
  return options;
}

const MONTH_OPTIONS = getMonthOptions();

// N-060 (доп.): месяц ещё не закончился — по нему рано предлагать какие-либо действия
// (симметрично логике вкладки «По клиникам», см. _month_ended в admin.py).
function isMonthEnded(year, month) {
  const lastDay = new Date(year, month, 0); // 0-й день следующего месяца = последний день этого
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return lastDay < today;
}

const MONTH_NAMES = [
  '', 'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];

const INVOICE_BADGE = {
  unpaid: { style: 'badgeGray', label: 'Не оплачено' },
  invoiced: { style: 'badgeYellow', label: 'Ожидает оплаты' },
  paid: { style: 'badgeGreen', label: 'Оплачено' },
};

function InvoiceBadge({ status, styles }) {
  const b = INVOICE_BADGE[status] || INVOICE_BADGE.unpaid;
  return <span style={styles[b.style]}>{b.label}</span>;
}

export default function Commissions() {
  const [view, setView] = useState('doctors'); // doctors | clinics
  const [selectedMonth, setSelectedMonth] = useState(MONTH_OPTIONS[0]);
  const key = localStorage.getItem('adminKey');
  const headers = { 'X-Admin-Key': key };

  // ── Вкладка «По врачам» ──
  const [commissions, setCommissions] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({ total_pending: 0, total_commissions: 0, total_platform: 0, doctors_count: 0, doctors_paid: 0, doctors_pending: 0 });
  const [offset, setOffset] = useState(0);
  const [sortField, setSortField] = useState('total');
  const [sortDir, setSortDir] = useState('desc');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({});
  const [payMsg, setPayMsg] = useState('');

  // ── N-065: чеки (СМЗ) / акты (ИП) по выплатам ──
  const [payoutModal, setPayoutModal] = useState(null); // { doctorId, doctorName, year, month, kind, status, reject_reason }
  const [payoutFile, setPayoutFile] = useState(null);
  const [payoutRejecting, setPayoutRejecting] = useState(false);
  const [payoutRejectReason, setPayoutRejectReason] = useState('');
  const [payoutBusy, setPayoutBusy] = useState(false);
  const [payoutErr, setPayoutErr] = useState('');

  const fetchData = useCallback(() => {
    axios.get(`${API}/admin/commissions`, {
      headers: { 'X-Admin-Key': key },
      params: {
        year: selectedMonth.year, month: selectedMonth.month,
        search: search || undefined, sort: sortField, order: sortDir,
        limit: LIMIT, offset,
      },
    }).then(r => { setCommissions(r.data.items); setTotal(r.data.total); setSummary(r.data.summary); });
  }, [selectedMonth, search, sortField, sortDir, offset, key]);

  // N-075: верхние сводные блоки должны отражать выбранный месяц независимо от активной
  // вкладки — поэтому обе стороны (врачи/клиники) грузятся всегда, а не только на своей вкладке.
  useEffect(() => {
    const t = setTimeout(() => fetchData(), 250);
    return () => clearTimeout(t);
  }, [fetchData]);

  const markDoctorPaid = async (doctorId) => {
    setPayMsg('');
    try {
      await axios.patch(
        `${API}/admin/commissions/doctor/${doctorId}/mark-paid`,
        {},
        { headers, params: { year: selectedMonth.year, month: selectedMonth.month } }
      );
      fetchData();
    } catch (e) {
      setPayMsg(e?.response?.data?.detail || 'Не удалось выплатить');
    }
  };

  const openPayoutModal = (doc) => {
    setPayoutModal({
      doctorId: doc.doctor_id, doctorName: doc.doctor_name,
      year: selectedMonth.year, month: selectedMonth.month,
      ...doc.payout_doc,
    });
    setPayoutFile(null); setPayoutRejecting(false); setPayoutRejectReason(''); setPayoutErr('');
  };
  const closePayoutModal = () => setPayoutModal(null);

  const downloadPayoutDoc = async () => {
    try {
      const r = await axios.get(`${API}/admin/commissions/doctor/${payoutModal.doctorId}/payout-document/file`, {
        headers, params: { year: payoutModal.year, month: payoutModal.month }, responseType: 'blob',
      });
      window.open(window.URL.createObjectURL(r.data), '_blank');
    } catch { setPayoutErr('Не удалось открыть файл'); }
  };

  const acceptReceipt = async () => {
    setPayoutBusy(true); setPayoutErr('');
    try {
      await axios.post(`${API}/admin/commissions/doctor/${payoutModal.doctorId}/payout-document/accept`, {}, {
        headers, params: { year: payoutModal.year, month: payoutModal.month },
      });
      closePayoutModal(); fetchData();
    } catch (e) { setPayoutErr(e?.response?.data?.detail || 'Не удалось принять чек'); }
    finally { setPayoutBusy(false); }
  };

  const rejectReceipt = async () => {
    if (!payoutRejectReason.trim()) { setPayoutErr('Укажите причину отказа'); return; }
    setPayoutBusy(true); setPayoutErr('');
    try {
      await axios.post(`${API}/admin/commissions/doctor/${payoutModal.doctorId}/payout-document/reject`, {}, {
        headers, params: { year: payoutModal.year, month: payoutModal.month, reason: payoutRejectReason },
      });
      closePayoutModal(); fetchData();
    } catch (e) { setPayoutErr(e?.response?.data?.detail || 'Не удалось отклонить чек'); }
    finally { setPayoutBusy(false); }
  };

  const uploadAct = async () => {
    if (!payoutFile) { setPayoutErr('Выберите файл'); return; }
    setPayoutBusy(true); setPayoutErr('');
    try {
      const fd = new FormData();
      fd.append('file', payoutFile);
      await axios.post(`${API}/admin/commissions/doctor/${payoutModal.doctorId}/payout-document/act`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
        params: { year: payoutModal.year, month: payoutModal.month },
      });
      closePayoutModal(); fetchData();
    } catch (e) { setPayoutErr(e?.response?.data?.detail || 'Не удалось загрузить акт'); }
    finally { setPayoutBusy(false); }
  };

  // Метка+доступность кнопки в строке врача (не открывает модалку сама — только описывает состояние)
  const payoutButtonState = (pd) => {
    if (pd.kind === 'receipt') {
      if (pd.status === 'accepted') return { label: 'Чек принят', disabled: false };
      if (pd.status === 'pending_review') return { label: 'Открыть чек', disabled: false };
      return { label: 'Открыть чек', disabled: true }; // not_uploaded / rejected
    }
    if (pd.status === 'accepted') return { label: 'Акт принят', disabled: false };
    if (pd.status === 'sent') return { label: 'Акт отправлен', disabled: false };
    return { label: 'Загрузить акт', disabled: false }; // not_uploaded
  };

  const toggleExpand = (doctorId) => {
    setExpanded(prev => ({ ...prev, [doctorId]: !prev[doctorId] }));
  };

  // N-056: развернуть/свернуть все строки на странице одним кликом — для быстрого
  // визуального поиска врача по данным первичных/повторных/квоты без ручного разворачивания.
  const allExpanded = commissions.length > 0 && commissions.every(doc => expanded[doc.doctor_id]);
  const toggleExpandAll = () => {
    if (allExpanded) {
      setExpanded({});
    } else {
      const next = {};
      commissions.forEach(doc => { next[doc.doctor_id] = true; });
      setExpanded(next);
    }
  };

  const toggleSort = (field) => {
    setOffset(0);
    if (sortField === field) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('asc'); }
  };
  const arrow = (field) => (sortField !== field ? '' : sortDir === 'asc' ? ' ▲' : ' ▼');

  // ── Вкладка «По клиникам» ──
  const [invItems, setInvItems] = useState([]);
  const [invTotal, setInvTotal] = useState(0);
  const [invSummary, setInvSummary] = useState({ total_amount: 0, total_owed: 0, unpaid_count: 0, invoiced_count: 0, paid_count: 0 });
  const [invOffset, setInvOffset] = useState(0);
  const [invSearch, setInvSearch] = useState('');
  const [invSort, setInvSort] = useState('total_amount');
  const [invOrder, setInvOrder] = useState('desc');
  const [invBusyId, setInvBusyId] = useState(null);
  const [invMsg, setInvMsg] = useState('');
  const [showMarkPaid, setShowMarkPaid] = useState(null); // row
  const [confirmTargetName, setConfirmTargetName] = useState('');

  const fetchInvoices = useCallback(() => {
    axios.get(`${API}/admin/clinic-invoices`, {
      headers: { 'X-Admin-Key': key },
      params: {
        year: selectedMonth.year, month: selectedMonth.month,
        search: invSearch || undefined, sort: invSort, order: invOrder,
        limit: LIMIT, offset: invOffset,
      },
    }).then(r => { setInvItems(r.data.items); setInvTotal(r.data.total); setInvSummary(r.data.summary); });
  }, [selectedMonth, invSearch, invSort, invOrder, invOffset, key]);

  useEffect(() => {
    const t = setTimeout(() => fetchInvoices(), 250);
    return () => clearTimeout(t);
  }, [fetchInvoices]);

  // N-054: пока хоть одна строка сверяется в фоне — мягко поллим, чтобы увидеть
  // реальный результат (confirmed/unconfirmed), а не полагаться на исходный ответ кнопки.
  useEffect(() => {
    if (view !== 'clinics') return;
    if (!invItems.some(r => r.period_status === 'reconciling')) return;
    const t = setTimeout(() => fetchInvoices(), 5000);
    return () => clearTimeout(t);
  }, [view, invItems, fetchInvoices]);

  const invToggleSort = (f) => { setInvOffset(0); if (invSort === f) setInvOrder(o => o === 'asc' ? 'desc' : 'asc'); else { setInvSort(f); setInvOrder('asc'); } };
  const invArrow = (f) => (invSort !== f ? '' : invOrder === 'asc' ? ' ▲' : ' ▼');

  const generateInvoice = async (row) => {
    setInvBusyId(`${row.target_type}-${row.clinic_id || row.network_id}`);
    setInvMsg('');
    try {
      await axios.post(`${API}/admin/clinic-invoices/generate`, {
        clinic_id: row.clinic_id, network_id: row.network_id,
        year: selectedMonth.year, month: selectedMonth.month,
      }, { headers });
      fetchInvoices();
    } catch (e) {
      setInvMsg(e?.response?.data?.detail || 'Не удалось сформировать счёт');
    } finally { setInvBusyId(null); }
  };

  const reconcilePeriod = async (row) => {
    setInvBusyId(`${row.target_type}-${row.clinic_id || row.network_id}`);
    setInvMsg('');
    try {
      await axios.post(`${API}/admin/clinic-invoices/reconcile`, {
        clinic_id: row.clinic_id, network_id: row.network_id,
        year: selectedMonth.year, month: selectedMonth.month,
      }, { headers });
      fetchInvoices();
    } catch (e) {
      setInvMsg(e?.response?.data?.detail || 'Не удалось сверить данные');
    } finally { setInvBusyId(null); }
  };

  const downloadInvoice = async (invoiceId, invoiceNumber) => {
    try {
      const r = await axios.get(`${API}/admin/clinic-invoices/${invoiceId}/download`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `${invoiceNumber || 'invoice'}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { setInvMsg('Не удалось скачать счёт'); }
  };

  const doMarkInvoicePaid = async () => {
    if (!showMarkPaid) return;
    setInvBusyId(`invoice-${showMarkPaid.invoice_id}`);
    setInvMsg('');
    try {
      await axios.patch(`${API}/admin/clinic-invoices/${showMarkPaid.invoice_id}/mark-paid`, {}, { headers });
      setShowMarkPaid(null); setConfirmTargetName('');
      fetchInvoices();
    } catch (e) {
      setInvMsg(e?.response?.data?.detail || 'Не удалось отметить оплату');
    } finally { setInvBusyId(null); }
  };

  const totalPending = summary.total_pending;
  const totalPlatform = summary.total_platform;
  const monthEnded = isMonthEnded(selectedMonth.year, selectedMonth.month);

  return (
    <Layout>
      <div style={styles.header}>
        <h1 style={styles.title}>Комиссии</h1>
      </div>

      <div style={styles.summary}>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>К выплате врачам</div>
          <div style={styles.summaryValue}>{fmtMoney(totalPending)} ₽</div>
          <div style={styles.summaryHint}>
            {summary.doctors_pending} {plural(summary.doctors_pending, ['врач', 'врача', 'врачей'])} {plural(summary.doctors_pending, ['ожидает', 'ожидают', 'ожидают'])} выплаты
          </div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>Комиссий врачей за месяц<StatusInfo def="commissions_doctors_total" /></div>
          <div style={{ ...styles.summaryValue, color: '#2EC4B6' }}>{fmtMoney(summary.total_commissions)} ₽</div>
          <div style={styles.summaryHint}>первичные + повторные, все статусы</div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>Доход Refero за месяц</div>
          <div style={{ ...styles.summaryValue, color: '#f59e0b' }}>{fmtMoney(totalPlatform)} ₽</div>
          <div style={styles.summaryHint}>10% от суммы комиссий врачам</div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>Врачей с комиссиями</div>
          <div style={{ ...styles.summaryValue, color: '#6366f1' }}>{summary.doctors_count}</div>
          <div style={styles.summaryHint}>
            {summary.doctors_paid} выплачено
          </div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>Сумма к выплате от клиник<StatusInfo def="commissions_clinics_to_pay" /></div>
          <div style={{ ...styles.summaryValue, color: '#8b5cf6' }}>{fmtMoney(invSummary.total_amount)} ₽</div>
          <div style={styles.summaryHint}>
            комиссии врачам + наш % за месяц
          </div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>Клиники должны Refero<StatusInfo def="commissions_clinics_debt" /></div>
          <div style={{ ...styles.summaryValue, color: '#f59e0b' }}>{fmtMoney(invSummary.total_owed)} ₽</div>
          <div style={styles.summaryHint}>
            {invSummary.unpaid_count} не выставлено · {invSummary.invoiced_count} ожидают · {invSummary.paid_count} оплачено
          </div>
        </div>
      </div>

      <div style={styles.tabs}>
        <div style={{ ...styles.tab, ...(view === 'doctors' ? styles.tabActive : {}) }} onClick={() => setView('doctors')}>По врачам</div>
        <div style={{ ...styles.tab, ...(view === 'clinics' ? styles.tabActive : {}) }} onClick={() => setView('clinics')}>По клиникам</div>
      </div>

      <div style={styles.headerControls}>
        <select
          style={styles.monthSelect}
          value={`${selectedMonth.year}-${selectedMonth.month}`}
          onChange={e => {
            const found = MONTH_OPTIONS.find(
              o => `${o.year}-${o.month}` === e.target.value
            );
            if (found) { setSelectedMonth(found); setOffset(0); setInvOffset(0); }
          }}
        >
          {MONTH_OPTIONS.map(o => (
            <option key={`${o.year}-${o.month}`} value={`${o.year}-${o.month}`}>
              {o.label.charAt(0).toUpperCase() + o.label.slice(1)}
            </option>
          ))}
        </select>
        {view === 'doctors' ? (
          <input
            style={styles.searchInput}
            placeholder="Поиск по врачу"
            value={search}
            onChange={e => { setSearch(e.target.value); setOffset(0); }}
          />
        ) : (
          <input
            style={styles.searchInput}
            placeholder="Поиск по клинике/сети"
            value={invSearch}
            onChange={e => { setInvSearch(e.target.value); setInvOffset(0); }}
          />
        )}
      </div>

      {view === 'doctors' && (
        <>
          {payMsg && <div style={styles.errBanner}>{payMsg}</div>}
          {commissions.length === 0 ? (
            <div style={styles.empty}>
              Комиссий за {MONTH_NAMES[selectedMonth.month]} {selectedMonth.year} нет
            </div>
          ) : (
            <div style={styles.table}>
              {/* Заголовок */}
              <div style={styles.thead}>
                <div style={styles.th}>
                  <button
                    style={styles.expandBtn}
                    onClick={toggleExpandAll}
                    title={allExpanded ? 'Свернуть все строки' : 'Развернуть все строки'}
                  >
                    {allExpanded ? '▲' : '▼'}
                  </button>
                </div>
                <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => toggleSort('name')}>Врач{arrow('name')}</div>
                <div style={styles.th}>Клиник</div>
                <div style={styles.th}>Первичные</div>
                <div style={styles.th}>Повторные</div>
                <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => toggleSort('total')}>Итого врачу{arrow('total')}</div>
                <div style={styles.th}>Refero</div>
                <div style={styles.th}>Статус<StatusInfo def="payment" /></div>
                <div style={styles.th}>Действие<StatusInfo def="doctor_payout_actions" /></div>
              </div>

              {commissions.map(doc => {
                const allInvoicesPaid = doc.clinics.every(c => c.invoice_status === 'paid');
                const canPay = doc.has_requisites && allInvoicesPaid;
                return (
                <div key={doc.doctor_id}>
                  {/* Строка доктора */}
                  <div
                    style={{
                      ...styles.row,
                      ...(expanded[doc.doctor_id] ? styles.rowExpanded : {}),
                    }}
                  >
                    {/* Кнопка раскрытия */}
                    <div style={styles.td}>
                      <button
                        style={styles.expandBtn}
                        onClick={() => toggleExpand(doc.doctor_id)}
                        title="Детализация по клиникам"
                      >
                        {expanded[doc.doctor_id] ? '▲' : '▼'}
                      </button>
                    </div>

                    {/* Имя врача — кликабельно */}
                    <div style={styles.td}>
                      <a href={`/doctors/${doc.doctor_id}`} style={styles.doctorLink}>
                        {doc.doctor_name}
                      </a>
                      <div style={styles.sub}>{doc.doctor_email}{doc.doctor_public_id ? ` · ${doc.doctor_public_id}` : ''}</div>
                    </div>

                    <div style={styles.td}>
                      <span style={styles.clinicsCount}>{doc.clinics.length}</span>
                    </div>

                    <div style={styles.td}>{fmtMoney(doc.total_primary)} ₽</div>
                    <div style={styles.td}>{fmtMoney(doc.total_repeat)} ₽</div>
                    <div style={{ ...styles.td, color: '#2EC4B6', fontWeight: 700 }}>
                      {fmtMoney(doc.total)} ₽
                    </div>
                    <div style={{ ...styles.td, color: '#f59e0b' }}>
                      {fmtMoney(doc.total_platform_fee)} ₽
                    </div>
                    <div style={styles.td}>
                      {!doc.is_paid
                        ? <span style={styles.badgeYellow}>Ожидает</span>
                        : doc.is_accepted
                          ? <span style={styles.badgeGreen}>Принято</span>
                          : <span style={styles.badgeBlue}>Выплачено</span>}
                    </div>
                    <div style={{ ...styles.td, display: 'flex', gap: 6, alignItems: 'center' }}>
                      {!monthEnded && <span style={styles.muted}>—</span>}
                      {monthEnded && !doc.is_paid && (
                        <button
                          style={{
                            ...styles.btnPay,
                            ...(canPay ? {} : (doc.has_requisites ? styles.btnDisabled : styles.btnDisabledRed)),
                          }}
                          onClick={() => markDoctorPaid(doc.doctor_id)}
                          disabled={!canPay}
                          title={!doc.has_requisites
                            ? 'У врача не заполнены реквизиты'
                            : (allInvoicesPaid ? '' : 'Не все клиники этого врача оплатили Refero за месяц')}
                        >
                          Выплатить
                        </button>
                      )}
                      {doc.is_paid && doc.payout_doc && (() => {
                        const btn = payoutButtonState(doc.payout_doc);
                        return (
                          <button
                            style={{ ...styles.btnGhostSmall, ...(btn.disabled ? styles.btnDisabled : {}) }}
                            disabled={btn.disabled}
                            title={doc.payout_doc.status === 'rejected' ? `Отклонён: ${doc.payout_doc.reject_reason}` : (btn.disabled ? 'Врач ещё не загрузил чек' : '')}
                            onClick={() => openPayoutModal(doc)}
                          >
                            {btn.label}
                          </button>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Accordion — детализация по клиникам */}
                  {expanded[doc.doctor_id] && (
                    <div style={styles.accordion}>
                      <div style={styles.accordionHeader}>
                        <div style={styles.accordionTh}>Клиника</div>
                        <div style={styles.accordionTh}>Первичные</div>
                        <div style={styles.accordionTh}>Повторные</div>
                        <div style={styles.accordionTh}>Сумма</div>
                        <div style={styles.accordionTh}>Refero</div>
                        <div style={styles.accordionTh}>Квота</div>
                        <div style={styles.accordionTh}>Оплата клиникой<StatusInfo def="clinic_invoice" /></div>
                      </div>
                      {doc.clinics.map(clinic => (
                        <div key={clinic.commission_id} style={styles.accordionRow}>
                          <div style={styles.accordionTd}>{clinic.clinic_name}</div>
                          <div style={styles.accordionTd}>{fmtMoney(clinic.primary_amount)} ₽</div>
                          <div style={styles.accordionTd}>{fmtMoney(clinic.repeat_amount)} ₽</div>
                          <div style={{ ...styles.accordionTd, color: '#2EC4B6' }}>
                            {fmtMoney(clinic.subtotal)} ₽
                          </div>
                          <div style={{ ...styles.accordionTd, color: '#f59e0b' }}>
                            {fmtMoney(clinic.platform_fee)} ₽
                          </div>
                          <div style={styles.accordionTd}>
                            {clinic.quota_fulfilled
                              ? <span style={styles.badgeGreen}>✅ {clinic.quota_count}{clinic.quota_min != null ? `/${clinic.quota_min}` : ''}</span>
                              : <span style={styles.badgeGray}>⏳ {clinic.quota_count}{clinic.quota_min != null ? `/${clinic.quota_min}` : ''}</span>}
                          </div>
                          <div style={styles.accordionTd}>
                            <InvoiceBadge status={clinic.invoice_status} styles={styles} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );})}
            </div>
          )}
          <ListPager total={total} limit={LIMIT} offset={offset} onPage={setOffset} />
        </>
      )}

      {view === 'clinics' && (
        <>
          {invMsg && <div style={styles.errBanner}>{invMsg}</div>}
          {invItems.length === 0 ? (
            <div style={styles.empty}>
              Начислений клиникам за {MONTH_NAMES[selectedMonth.month]} {selectedMonth.year} нет
            </div>
          ) : (
            <div style={styles.table}>
              <div style={{ ...styles.thead, gridTemplateColumns: invCols }}>
                <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => invToggleSort('target_name')}>Клиника/сеть{invArrow('target_name')}</div>
                <div style={styles.th}>Врачам</div>
                <div style={styles.th}>Refero</div>
                <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => invToggleSort('total_amount')}>Итого{invArrow('total_amount')}</div>
                <div style={styles.th}>Сверка<StatusInfo def="period_reconciliation" /></div>
                <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => invToggleSort('status')}>Статус{invArrow('status')}<StatusInfo def="clinic_invoice" /></div>
                <div style={styles.th}>№ счёта</div>
                <div style={styles.th}>Действие<StatusInfo def="clinic_invoice_actions" /></div>
              </div>
              {invItems.map(row => {
                const busyKey1 = `${row.target_type}-${row.clinic_id || row.network_id}`;
                const busyKey2 = `invoice-${row.invoice_id}`;
                const busy = invBusyId === busyKey1 || invBusyId === busyKey2;
                const confirmed = row.period_status === 'confirmed';
                return (
                  <div key={busyKey1} style={{ ...styles.row, gridTemplateColumns: invCols }}>
                    <div style={styles.td}>
                      <a href={row.target_type === 'network' ? `/networks/${row.network_id}` : `/clinics/${row.clinic_id}`} style={styles.doctorLink}>
                        {row.target_name}
                      </a>
                    </div>
                    <div style={styles.td}>{fmtMoney(row.doctors_amount)} ₽</div>
                    <div style={{ ...styles.td, color: '#f59e0b' }}>{fmtMoney(row.platform_fee)} ₽</div>
                    <div style={{ ...styles.td, color: '#2EC4B6', fontWeight: 700 }}>{fmtMoney(row.total_amount)} ₽</div>
                    <div style={styles.td} title={row.period_error || ''}>
                      {row.period_status === 'reconciling'
                        ? <span style={styles.badgeYellow}>Сверка идёт…</span>
                        : confirmed
                          ? <span style={styles.badgeGreen}>Подтверждён</span>
                          : <span style={styles.badgeGray}>Не подтверждён</span>}
                    </div>
                    <div style={styles.td}><InvoiceBadge status={row.status} styles={styles} /></div>
                    <div style={{ ...styles.td, fontFamily: 'monospace', fontSize: 12, color: '#94a3b8' }}>{row.invoice_number || '—'}</div>
                    <div style={{ ...styles.td, display: 'flex', gap: 6, alignItems: 'center' }}>
                      {!row.month_ended && <span style={styles.muted}>—</span>}
                      {row.month_ended && row.status === 'unpaid' && row.period_status === 'reconciling' && (
                        <span style={styles.muted}>Сверка выполняется…</span>
                      )}
                      {row.month_ended && row.status === 'unpaid' && confirmed && (
                        <button style={styles.btnPay} disabled={busy} onClick={() => generateInvoice(row)}>Сформировать счёт</button>
                      )}
                      {row.month_ended && row.status === 'unpaid' && !confirmed && row.period_status !== 'reconciling' && (
                        <button style={styles.btnGhostSmall} disabled={busy} onClick={() => reconcilePeriod(row)}>Повторить сверку</button>
                      )}
                      {row.status !== 'unpaid' && (
                        <button style={styles.btnGhostSmall} disabled={busy} onClick={() => downloadInvoice(row.invoice_id, row.invoice_number)}>Скачать счёт</button>
                      )}
                      {row.status === 'invoiced' && (
                        <button style={styles.btnPay} disabled={busy} onClick={() => { setShowMarkPaid(row); setConfirmTargetName(''); }}>Оплачено</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <ListPager total={invTotal} limit={LIMIT} offset={invOffset} onPage={setInvOffset} />
        </>
      )}

      {showMarkPaid && (
        <div style={styles.modalOverlay} onClick={() => setShowMarkPaid(null)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Отметить счёт оплаченным?</h3>
            <p style={styles.modalText}>
              Подтвердите, что «{showMarkPaid.target_name}» перевела Refero {fmtMoney(showMarkPaid.total_amount)} ₽
              по счёту {showMarkPaid.invoice_number}.
              <br /><br />Для подтверждения введите точное название:
              <br /><b style={{ color: '#e2e8f0' }}>{showMarkPaid.target_name}</b>
            </p>
            <input style={styles.modalInput} placeholder="Название клиники/сети" value={confirmTargetName}
              onChange={e => setConfirmTargetName(e.target.value)} autoFocus />
            <div style={styles.modalActions}>
              <button style={styles.btnGhost} onClick={() => setShowMarkPaid(null)}>Отмена</button>
              <button
                style={{ ...styles.btn, ...(confirmTargetName.trim() === showMarkPaid.target_name.trim() ? {} : styles.btnDisabled) }}
                onClick={doMarkInvoicePaid}
                disabled={confirmTargetName.trim() !== showMarkPaid.target_name.trim()}
              >
                Оплачено
              </button>
            </div>
          </div>
        </div>
      )}

      {payoutModal && (
        <div style={styles.modalOverlay} onClick={closePayoutModal}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>
              {payoutModal.kind === 'receipt' ? 'Чек' : 'Акт'} — {payoutModal.doctorName}
            </h3>
            {payoutErr && <div style={styles.errText}>{payoutErr}</div>}

            {payoutModal.kind === 'receipt' && (
              <>
                {payoutModal.status === 'not_uploaded' && (
                  <p style={styles.modalText}>Врач ещё не загрузил чек за этот месяц.</p>
                )}
                {payoutModal.status === 'rejected' && (
                  <p style={styles.modalText}>Чек был отклонён: «{payoutModal.reject_reason}». Ожидаем повторной загрузки от врача.</p>
                )}
                {(payoutModal.status === 'pending_review' || payoutModal.status === 'accepted') && (
                  <>
                    <button style={styles.btnGhost} onClick={downloadPayoutDoc}>Скачать чек</button>
                    {payoutModal.status === 'accepted' && (
                      <p style={{ ...styles.modalText, marginTop: 12 }}><span style={styles.badgeGreen}>Принято</span></p>
                    )}
                    {payoutModal.status === 'pending_review' && !payoutRejecting && (
                      <div style={styles.modalActions}>
                        <button style={styles.btnGhost} onClick={() => setPayoutRejecting(true)}>Отклонить</button>
                        <button style={styles.btn} disabled={payoutBusy} onClick={acceptReceipt}>Принять</button>
                      </div>
                    )}
                    {payoutRejecting && (
                      <>
                        <textarea
                          style={styles.modalTextarea} rows={3} placeholder="Причина отказа"
                          value={payoutRejectReason} onChange={e => setPayoutRejectReason(e.target.value)}
                        />
                        <div style={styles.modalActions}>
                          <button style={styles.btnGhost} onClick={() => setPayoutRejecting(false)}>Назад</button>
                          <button style={styles.btn} disabled={payoutBusy} onClick={rejectReceipt}>Отклонить чек</button>
                        </div>
                      </>
                    )}
                  </>
                )}
              </>
            )}

            {payoutModal.kind === 'act' && (
              <>
                {(payoutModal.status === 'sent' || payoutModal.status === 'accepted') && (
                  <button style={styles.btnGhost} onClick={downloadPayoutDoc}>Скачать текущий акт</button>
                )}
                {payoutModal.status === 'accepted' ? (
                  <p style={{ ...styles.modalText, marginTop: 12 }}><span style={styles.badgeGreen}>Принято врачом</span></p>
                ) : (
                  <>
                    <input
                      type="file" accept=".pdf,.jpg,.jpeg,.png"
                      onChange={e => setPayoutFile(e.target.files[0])}
                      style={{ marginTop: 12, color: '#e2e8f0', fontSize: 13 }}
                    />
                    <div style={styles.modalActions}>
                      <button style={styles.btnGhost} onClick={closePayoutModal}>Отмена</button>
                      <button style={styles.btn} disabled={payoutBusy} onClick={uploadAct}>
                        {payoutModal.status === 'sent' ? 'Перезалить акт' : 'Загрузить акт'}
                      </button>
                    </div>
                  </>
                )}
              </>
            )}

            {((payoutModal.kind === 'receipt' && (payoutModal.status === 'not_uploaded' || payoutModal.status === 'rejected'))
              || (payoutModal.kind === 'act' && payoutModal.status === 'accepted')) && (
              <div style={styles.modalActions}>
                <button style={styles.btnGhost} onClick={closePayoutModal}>Закрыть</button>
              </div>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
}

const invCols = '2fr 1fr 1fr 1fr 1.2fr 1.2fr 1.3fr 1.8fr';

const styles = {
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, gap: 16 },
  headerControls: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 },
  title: { color: 'white', fontSize: 24, fontWeight: 700, margin: 0 },
  monthSelect: {
    background: '#1e293b', color: 'white', border: '1px solid #334155',
    borderRadius: 8, padding: '8px 14px', fontSize: 14, cursor: 'pointer',
    outline: 'none',
  },
  summary: { display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' },
  summaryCard: { background: '#1e293b', borderRadius: 12, padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 4, minWidth: 180 },
  summaryLabel: { fontSize: 12, color: '#64748b' },
  summaryValue: { fontSize: 24, fontWeight: 700, color: '#2EC4B6' },
  summaryHint: { fontSize: 11, color: '#475569' },
  tabs: { display: 'flex', gap: 4, borderBottom: '1px solid #334155', marginBottom: 20 },
  tab: { padding: '10px 18px', color: '#94a3b8', fontSize: 14, fontWeight: 600, cursor: 'pointer', borderBottom: '2px solid transparent', marginBottom: -1 },
  tabActive: { color: '#2EC4B6', borderBottom: '2px solid #2EC4B6' },
  empty: { color: '#64748b', fontSize: 15, textAlign: 'center', padding: '60px 0' },
  errBanner: { background: '#450a0a', color: '#fca5a5', border: '1px solid #7f1d1d', borderRadius: 10, padding: '10px 16px', fontSize: 13, marginBottom: 16 },
  table: { background: '#1e293b', borderRadius: 12, overflow: 'auto' },
  thead: {
    display: 'grid',
    gridTemplateColumns: '40px 2fr 80px 1fr 1fr 1.2fr 1fr 1fr 1.2fr',
    padding: '12px 20px', borderBottom: '1px solid #334155', minWidth: 800,
  },
  th: { fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 },
  thClickable: { cursor: 'pointer', userSelect: 'none' },
  searchInput: { background: '#1e293b', color: 'white', border: '1px solid #334155', borderRadius: 8, padding: '8px 14px', fontSize: 14, outline: 'none' },
  row: {
    display: 'grid',
    gridTemplateColumns: '40px 2fr 80px 1fr 1fr 1.2fr 1fr 1fr 1.2fr',
    padding: '14px 20px', borderBottom: '1px solid #0f172a',
    alignItems: 'center', minWidth: 800,
    transition: 'background 0.1s',
  },
  rowExpanded: { background: '#162032', borderBottom: 'none' },
  td: { fontSize: 13, color: '#e2e8f0' },
  sub: { fontSize: 11, color: '#64748b', marginTop: 2 },
  doctorLink: { color: '#e2e8f0', textDecoration: 'none', fontWeight: 600, fontSize: 13 },
  clinicsCount: { background: '#0f172a', color: '#94a3b8', borderRadius: 20, padding: '2px 10px', fontSize: 12, display: 'inline-block' },
  expandBtn: {
    background: 'none', border: '1px solid #334155', color: '#64748b',
    borderRadius: 4, width: 24, height: 24, cursor: 'pointer',
    fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  // Accordion
  accordion: {
    background: '#0d1929', borderBottom: '1px solid #0f172a',
    padding: '0 20px 12px 60px', minWidth: 800,
  },
  accordionHeader: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr 1.3fr',
    padding: '8px 0 6px', borderBottom: '1px solid #1e293b', marginBottom: 4,
  },
  accordionTh: { fontSize: 10, color: '#475569', fontWeight: 600, textTransform: 'uppercase' },
  accordionRow: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr 1.3fr',
    padding: '8px 0', borderBottom: '1px solid #0f172a',
  },
  accordionTd: { fontSize: 12, color: '#94a3b8' },
  badgeGreen: { background: '#052e16', color: '#4ade80', padding: '3px 8px', borderRadius: 20, fontSize: 11, display: 'inline-block' },
  badgeBlue: { background: '#1e3a5f', color: '#60a5fa', padding: '3px 8px', borderRadius: 20, fontSize: 11, display: 'inline-block' },
  badgeGray: { background: '#1e293b', color: '#64748b', padding: '3px 8px', borderRadius: 20, fontSize: 11, display: 'inline-block', border: '1px solid #334155' },
  badgeYellow: { background: '#422006', color: '#fbbf24', padding: '3px 8px', borderRadius: 20, fontSize: 11, display: 'inline-block' },
  btnPay: { background: '#2EC4B6', color: 'white', border: 'none', padding: '6px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600 },
  btnGhostSmall: { background: 'transparent', color: '#94a3b8', border: '1px solid #334155', padding: '6px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer' },
  btnDisabled: { opacity: 0.4, cursor: 'default' },
  btnDisabledRed: { background: '#450a0a', color: '#fca5a5', border: '1px solid #7f1d1d', cursor: 'default' },
  muted: { color: '#475569', fontSize: 12 },
  btn: { background: '#2EC4B6', color: '#0f172a', border: 'none', borderRadius: 6, padding: '7px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnGhost: { background: 'transparent', color: '#94a3b8', border: '1px solid #334155', borderRadius: 6, padding: '7px 16px', fontSize: 13, cursor: 'pointer' },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 },
  modal: { background: '#1e293b', borderRadius: 12, padding: 24, width: 440, maxWidth: '90vw', border: '1px solid #334155' },
  modalTitle: { color: 'white', fontSize: 18, fontWeight: 700, margin: '0 0 12px' },
  modalText: { color: '#94a3b8', fontSize: 13, lineHeight: 1.5, margin: '0 0 16px' },
  modalInput: { width: '100%', boxSizing: 'border-box', background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 6, padding: '9px 12px', fontSize: 14, outline: 'none', marginBottom: 16 },
  modalTextarea: { width: '100%', boxSizing: 'border-box', background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 6, padding: '9px 12px', fontSize: 14, outline: 'none', marginTop: 12, fontFamily: 'inherit', resize: 'vertical' },
  errText: { color: '#fca5a5', fontSize: 13, marginBottom: 12 },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 },
};
